if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();

      this.open = false;
      this.suppressFocusReturn = false;

      this.handleToggleClick = this.toggle.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    }

    connectedCallback() {
      this.elForm = this.querySelector('form');
      this.elInput = this.querySelector('[data-input]');
      this.elToggle = this.querySelector('[data-toggle]');
      this.elPanel = this.querySelector('[data-panel]');
      this.elFallback = this.querySelector('[data-fallback]');

      if (!this.elForm || !this.elToggle || !this.elPanel) return;

      if (this.elFallback) {
        this.elFallback.setAttribute('hidden', '');

        for (const elField of this.elFallback.querySelectorAll('select, button, input')) {
          elField.disabled = true;
        }

        this.elFallback.querySelector('[data-fallback-select]')?.removeAttribute('name');
      }

      this.elToggle.addEventListener('click', this.handleToggleClick);

      for (const elOption of this.#getOptions()) {
        elOption.addEventListener('click', this.handleOptionClick);
      }
    }

    disconnectedCallback() {
      this.#removeDocumentListeners();
    }

    #getOptions() {
      return [...this.elPanel.querySelectorAll('[role="option"]')];
    }

    #getSelectedIndex() {
      const options = this.#getOptions();
      const selectedIndex = options.findIndex(el => el.getAttribute('aria-selected') === 'true');
      return selectedIndex >= 0 ? selectedIndex : 0;
    }

    #getActiveIndex() {
      const options = this.#getOptions();
      const activeIndex = options.findIndex(el => el.tabIndex === 0);
      return activeIndex >= 0 ? activeIndex : this.#getSelectedIndex();
    }

    #setActiveIndex(index) {
      const options = this.#getOptions();
      if (options.length === 0) return;

      const nextIndex = Math.max(0, Math.min(index, options.length - 1));

      for (const [i, elOption] of options.entries()) {
        elOption.tabIndex = i === nextIndex ? 0 : -1;
      }

      options[nextIndex].focus({ preventScroll: true });
      options[nextIndex].scrollIntoView({ block: 'nearest' });
    }

    #addDocumentListeners() {
      document.addEventListener('keydown', this.handleDocumentKeydown, true);
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    #removeDocumentListeners() {
      document.removeEventListener('keydown', this.handleDocumentKeydown, true);
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    toggle() {
      this.setOpen(!this.open);
    }

    setOpen(nextOpen) {
      if (this.open === nextOpen) return;

      this.open = nextOpen;
      this.classList.toggle('open', this.open);
      this.elToggle.setAttribute('aria-expanded', String(this.open));

      if (this.open) {
        this.elPanel.removeAttribute('inert');
        this.#addDocumentListeners();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#setActiveIndex(this.#getSelectedIndex());
          });
        });
      } else {
        this.elPanel.setAttribute('inert', '');
        this.#removeDocumentListeners();

        if (this.contains(document.activeElement) && !this.suppressFocusReturn) {
          this.elToggle.focus({ preventScroll: true });
        }

        this.suppressFocusReturn = false;
      }
    }

    handleOptionClick(event) {
      event.preventDefault();
      this.#selectOption(event.currentTarget);
    }

    #selectOption(elOption) {
      if (!this.elForm || !this.elInput) return;

      const countryCode = elOption.dataset.value;
      if (!countryCode) return;

      for (const el of this.#getOptions()) {
        el.setAttribute('aria-selected', el === elOption ? 'true' : 'false');
      }

      this.elInput.value = countryCode;
      this.#sanitizeReturnTo();
      this.elForm.submit();
    }

    #sanitizeReturnTo() {
      const elReturnTo = this.elForm.querySelector('input[name="return_to"]');
      if (!elReturnTo?.value) return;

      try {
        const url = new URL(elReturnTo.value, window.location.origin);
        url.searchParams.delete('country');
        elReturnTo.value = `${url.pathname}${url.search}`;
      } catch {
        elReturnTo.value = window.location.pathname;
      }
    }

    #isWithinComponent(element) {
      return element instanceof Node && (this.contains(element) || element === this.elToggle);
    }

    handleDocumentKeydown(event) {
      if (!this.open) {
        if (!this.#isWithinComponent(event.target)) return;

        if (event.target === this.elToggle && (event.key === ' ' || event.key === 'Enter' || event.key === 'ArrowDown')) {
          event.preventDefault();
          this.setOpen(true);
        }

        return;
      }

      if (!this.#isWithinComponent(document.activeElement)) return;

      const options = this.#getOptions();

      if (event.key === 'Escape') {
        event.preventDefault();
        this.setOpen(false);
        return;
      }

      if (event.key === 'Tab') {
        requestAnimationFrame(() => {
          if (this.open && !this.#isWithinComponent(document.activeElement)) {
            this.suppressFocusReturn = true;
            this.setOpen(false);
          }
        });
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();

        if (document.activeElement === this.elToggle) {
          this.#setActiveIndex(this.#getSelectedIndex());
          return;
        }

        this.#setActiveIndex(this.#getActiveIndex() + 1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        if (document.activeElement === this.elToggle) {
          this.#setActiveIndex(this.#getSelectedIndex());
          return;
        }

        this.#setActiveIndex(this.#getActiveIndex() - 1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.#setActiveIndex(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.#setActiveIndex(options.length - 1);
        return;
      }

      const elOption = event.target.closest('[role="option"]');
      if (!elOption || !this.elPanel.contains(elOption)) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#selectOption(elOption);
      }
    }

    handleDocumentPointerDown(event) {
      if (this.contains(event.target)) return;

      this.suppressFocusReturn = true;
      this.setOpen(false);
    }
  }

  customElements.define('localization-form', LocalizationForm);
}
